import { useCallback, useContext, useEffect, useState } from "react";
import { DoubleSide } from 'three';
import ModelElements from "../ModelElements";
import type { ProductModelProps } from "../ModelViewerProduct";
import ProductTextureContext from "../_contexts/ProductTextureContext";
import useGLB, { type HasStandardMaterial } from "../_hooks/useGLB";
import { FindMaterial } from "../../Common/Utils/ThreeTools";
import { applyFabricMaterialProfile } from '../fabricMaterialProfiles';

const FABRIC_TEXTURE_REPEAT = 18

export default function RuffleScrunchie(props: ProductModelProps) {
    const url = new URL('@resources/3d-models/glb/ruffle-scrunchie.glb', import.meta.url);
    const model = useGLB(url)
    const [productTexture] = useContext(ProductTextureContext)
    const [forceUpdate, setForceUpdate] = useState(0)

    // Turn the underside upward before Stage centers it on the shadow surface.
    model.scene.rotation.x = props.model === 'ruffle-scrunchie-bottom' ? Math.PI : 0

    const optionFabric = props.options.get('fabric') ?? 'matte-crepe'
    const threadColor = props.options.get('thread-color') ?? 'black'

    useEffect(() => {
        const material = FindMaterial("edge", model.scene.children)
        if(material) {material.color.setHex({
                'white': 0xeeeeee,
                'light-grey': 0xa4a4a4,
                'dark-grey': 0x555555,
                'black': 0x2a2a2a
            }[threadColor] ?? 0x2a2a2a)}
    }, [threadColor, model.scene])

    const processMaterial = useCallback((object: HasStandardMaterial) => {
        object.material.toneMapped = false
        object.material.depthWrite = true
        object.material.transparent = true
        object.material.color.set(1, 1, 1)
        object.material.alphaToCoverage = false
        object.material.side = DoubleSide
        object.material.opacity = 1

        // For some reason three doesn't load the alpha in the right place
        if (!object.material.alphaMap) {
            object.material.alphaMap = object.material.map
            object.material.map = null
        }
           
        object.material.map = productTexture
        object.material.map.flipY = false
        object.material.map.needsUpdate = true
        
        // Can't bake maps cause texel size would be too big so just scale a seamless one instead
        if (object.material.normalMap) {
            object.material.normalMap.repeat.set(FABRIC_TEXTURE_REPEAT, FABRIC_TEXTURE_REPEAT)
            object.material.normalScale.x = 2
            object.material.normalScale.y = 2
        }
        if (object.material.alphaMap) {
            object.material.alphaMap.repeat.set(FABRIC_TEXTURE_REPEAT, FABRIC_TEXTURE_REPEAT)
        }
        if (object.material.metalnessMap) {
            object.material.metalnessMap.repeat.set(FABRIC_TEXTURE_REPEAT, FABRIC_TEXTURE_REPEAT)
        }
        if (object.material.roughnessMap) {
            object.material.roughnessMap.repeat.set(FABRIC_TEXTURE_REPEAT, FABRIC_TEXTURE_REPEAT)
        }
        applyFabricMaterialProfile(object.material, optionFabric)
    }, [productTexture, optionFabric])

    // KHR variant materials load lazily from the cached parser. The GLB
    // instance facade converts them to viewer-owned materials before this
    // product-specific processing callback is allowed to mutate them.
    useEffect(() => {
        const variants = model.variants
        if(!variants) return

        const controller = new AbortController()
        void variants.select(optionFabric, processMaterial).then(() => {
            if(!controller.signal.aborted) {
                setForceUpdate(current => current + 1)
            }
        })

        return () => {
            controller.abort()
        }
    }, [
        model.variants,
        optionFabric,
        processMaterial,
        props.variant,
        threadColor,
    ])

    return <ModelElements
        forceUpdate={`${props.model ?? ''}${optionFabric}${threadColor}${forceUpdate}`}
        scene={model.scene}
    />
}
